'use client';
import React from 'react';
import { MenuInterface, MenuProps, GroupItem, MenuItem } from './interfaces';
import { Icon } from '@benzinga/core-ui';

import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons/faGraduationCap';
import { faWrench } from '@fortawesome/pro-solid-svg-icons/faWrench';
import { faChartMixed } from '@fortawesome/pro-solid-svg-icons/faChartMixed';
import { faChalkboard } from '@fortawesome/pro-solid-svg-icons/faChalkboard';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import classNames from 'classnames';
import { convertToRelativeUrl } from './_mockMenu';
import { ButtonBzPro } from '../BenzingaProButton';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { useHydrate } from '@benzinga/hooks';
import { appEnvironment, appName } from '@benzinga/utils';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { handleMenuItemsClickEvent } from '../../utils';
import styles from './Menu.module.scss';
import { getNavMenuItemHref } from '../../premiumServiceLinks';
import { usePremiumServiceAccess } from '../../hooks/usePremiumServiceAccess';

interface MenuItemGroupProps {
  groups: GroupItem[];
}

export const Menu: React.FC<MenuProps> = React.memo(({ menu, secondary = false }) => {
  const hasSubmenu = menu.groups?.length || menu.subnav;
  const isTradingSchool = menu.label === 'Trading School';
  const isLoggedIn = useHydrate(useIsUserLoggedIn(), false);
  const isMoneyApp = appEnvironment().isApp(appName.money);

  return (
    <div className={`${styles.menuWrapper} menu-wrapper ${secondary ? 'secondary' : 'primary'}`}>
      {secondary ? (
        <a
          aria-label={`This opens ${menu.label} in a new tab`}
          className={classNames(styles.primaryToolbarButton, 'others-btn primary-toolbar-button')}
          data-exclude-track="true"
          href={isLoggedIn && menu.logged_in_href ? menu.logged_in_href : menu.href}
          onClick={() => handleMenuItemsClickEvent('menu_click', 'main_menu', menu.label)}
          rel="noreferrer"
          style={{ backgroundColor: menu.color as string, minWidth: menu.width ?? 'fit-content' }}
          target="_blank"
          title={menu.label}
        >
          {menu.label === 'Tools' && <Icon className="text-xl pr-1" icon={faWrench} />}
          {menu.label === 'My Stocks' && <Icon className="text-xl pr-1" icon={faChartMixed} />}
          {menu.label === 'Research' && <Icon className="text-xl pr-2" icon={faChalkboard} />}
          {menu.label}
        </a>
      ) : (
        <MenuItemLink
          className={classNames(`menu-item-link`, {
            [`${menu.className}`]: !!menu.className,
            initial: !isTradingSchool,
            'trading-school': isTradingSchool,
          })}
          data-action="Navigation Main Menu Click"
          data-exclude-track="true"
          href={convertToRelativeUrl(isLoggedIn && menu.logged_in_href ? menu.logged_in_href : menu.href)}
          onClick={() => handleMenuItemsClickEvent('menu_click', 'main_menu', menu.label)}
          style={{ minWidth: menu.width ?? 'fit-content' }}
          title={menu.label}
        >
          {menu.label} {isTradingSchool && <Icon className="ml-2 text-2xl" icon={faGraduationCap} />}
          {hasSubmenu && menu.subGroupChevron && (
            <Icon
              className="ml-2 test-xs text-gray-400 menu-item-link-chevron"
              icon={faChevronDown}
              style={{ height: '10px', width: '9px' }}
              trimX={true}
              trimY={true}
            />
          )}
        </MenuItemLink>
      )}
      {hasSubmenu ? (
        <div
          className={classNames(
            styles.subNavWrapper,
            'sub-nav-wrapper subnav',
            { 'has-groups': !!menu?.groups },
            { 'money-app': isMoneyApp },
            { 'tools-group': menu.label === 'Tools' },
          )}
        >
          <div className={`${styles.subGroupWrapper} sub-group-wrapper`}>
            {menu.label === 'Tools' && (
              <ButtonBzPro
                label="Free Benzinga Pro Trial"
                url="https://pro.benzinga.com/register/?utm_source=tools-bz-pro-free-trial"
                variant="trial"
              />
            )}
            {menu.subnav ? <MenuItems items={menu.subnav.links} /> : null}
            {menu.groups?.length ? <MenuItemGroups groups={menu.groups} /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
});

interface MenuItemsProps {
  items: MenuItem[];
}

export const MenuItems: React.FC<MenuItemsProps> = ({ items }) => {
  const isLoggedIn = useHydrate(useIsUserLoggedIn(), false);
  const premiumServiceAccess = usePremiumServiceAccess(isLoggedIn);

  const getItemHref = (item: MenuItem) => getNavMenuItemHref(item, isLoggedIn, premiumServiceAccess);

  return (
    <div className="menu-items">
      <div className={`${styles.menuItemWrapper} menu-item-wrapper`}>
        {items?.map((item, i: number) => {
          const dataAction = item.action ? item.action : 'Navigation Sub-Menu Click';
          return (
            <div
              className={classNames(
                styles.dropdownMenuItem,
                item.highlight ? 'highlight dropdown-menu-item' : 'dropdown-menu-item',
              )}
              key={i}
              style={
                {
                  '--highlight-color': item.highlight || 'unset',
                  '--highlight-hover-color': item.highlight ? '#ffffff' : 'unset',
                  '--highlight-weight': item.highlight ? '600' : 'unset',
                } as React.CSSProperties
              }
            >
              <a
                data-action={dataAction}
                data-exclude-track="true"
                href={convertToRelativeUrl(getItemHref(item))}
                onClick={() => handleMenuItemsClickEvent('menu_click', 'main_menu', item.label)}
                title={item.title}
              >
                {item.label}
              </a>
              {item?.subnav?.links && (
                <>
                  <span className="more-nav-menu">
                    <Icon className="arrow-right-icon" icon={faAngleRight} />
                  </span>
                  <div className="third-level-menu">
                    {item?.subnav?.links ? <MenuItems items={item.subnav.links} /> : null}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MenuItemGroups: React.FC<MenuItemGroupProps> = ({ groups }) => {
  return (
    <div className={`${styles.groupList} group-list`}>
      {groups?.map((group, i) => {
        return (
          <div key={i}>
            <div className={`${styles.groupListItemLabel} group-list-item-label`}>{group.label}</div>
            <div>
              <MenuItems items={group.links} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const MenuItemLink = <T extends React.ElementType = 'a'>({
  as,
  className,
  ...props
}: { as?: T } & Omit<React.ComponentPropsWithRef<T>, 'as'>) => {
  const Component = as ?? 'a';
  return <Component className={classNames(styles.menuItemLink, className)} {...props} />;
};

export const MoreMenuItemsDropdown = ({ isVisible, menus }) => {
  const hiddenMenuItems = menus.primary?.slice(2) || [];

  return (
    <div
      className={classNames(styles.moreMenuItemsDropdownContainer, 'more-menu-items-dropdown-container', {
        [styles.visible]: isVisible,
      })}
    >
      {hiddenMenuItems.map((menu: MenuInterface, i: number) => (
        <Menu key={i} menu={menu} />
      ))}
    </div>
  );
};
